import { Menu, X, ChevronDown, Calculator, Grid3x3, DollarSign, Sun, Battery, MapPin, Building2 } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Logo } from './Logo';

export function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [toolsDropdownOpen, setToolsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'Grids', path: '/grids' },
    { label: 'Renewables', path: '/renewables' },
    { label: 'Oil & Gas', path: '/oil-gas' },
    { label: 'Tools', path: '/tools', hasDropdown: true },
    { label: 'Articles', path: '/articles' },
    { label: 'Solar Quotes', path: '/solar-quotes' },
  ];

  const toolsMenu = [
    { label: 'Electricity Bill Calculator', path: '/tools/bill-calculator', icon: Calculator },
    { label: 'Tariff Band Checker', path: '/tools/tariff-checker', icon: Grid3x3 },
    { label: 'Outage Cost Calculator', path: '/tools/outage-calculator', icon: DollarSign },
    { label: 'Solar System Sizing', path: '/tools/solar-sizing', icon: Sun },
    { label: 'Home Energy Audit', path: '/tools/energy-audit', icon: Battery },
    { label: 'Refinery Tracker', path: '/tools/refinery-tracker', icon: MapPin },
    { label: 'DisCo Directory', path: '/tools/disco-directory', icon: Building2 },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setToolsDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setToolsDropdownOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/' && location.pathname !== '/') return false;
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="sticky top-0 z-[2000] bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <Logo />
            <div className="flex flex-col items-start">
              <span className="text-xl font-bold text-[#008751]">Power.ng</span>
              <span className="text-xs text-gray-600 hidden sm:block">Nigeria's Energy Hub</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.hasDropdown ? (
                <div key={link.label} className="relative" ref={dropdownRef}>
                  <Link
                    to={link.path}
                    onMouseEnter={() => setToolsDropdownOpen(true)}
                    className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-[#008751] ${isActive(link.path) || toolsDropdownOpen ? 'text-[#008751]' : 'text-gray-700'
                      }`}
                  >
                    {link.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${toolsDropdownOpen ? 'rotate-180' : ''}`} />
                  </Link>

                  {/* Dropdown Menu */}
                  {toolsDropdownOpen && (
                    <div
                      className="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 py-2"
                      onMouseEnter={() => setToolsDropdownOpen(true)}
                      onMouseLeave={() => setToolsDropdownOpen(false)}
                    >
                      {toolsMenu.map((tool) => {
                        const Icon = tool.icon;
                        return (
                          <Link
                            key={tool.path}
                            to={tool.path}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                            onClick={() => setToolsDropdownOpen(false)}
                          >
                            <div className="w-8 h-8 bg-[#E8F5E9] rounded-lg flex items-center justify-center flex-shrink-0">
                              <Icon className="w-4 h-4 text-[#008751]" />
                            </div>
                            <span className="text-sm text-gray-700 hover:text-[#008751]">{tool.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.label}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-[#008751] ${isActive(link.path) ? 'text-[#008751]' : 'text-gray-700'
                    }`}
                >
                  {link.label}
                </Link>
              )
            ))}
            <Link
              to="/power-manager"
              className="bg-[#C1272D] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#a02024] transition-colors"
            >
              Play Power Manager
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-gray-700" />
            ) : (
              <Menu className="w-6 h-6 text-gray-700" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div id="mobile-navigation" className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              link.hasDropdown ? (
                <div key={link.label}>
                  <Link
                    to={link.path}
                    className={`block w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive(link.path)
                      ? 'bg-[#E8F5E9] text-[#008751]'
                      : 'text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    {link.label}
                  </Link>
                  {/* Mobile Tools Submenu */}
                  <div className="ml-4 mt-2 space-y-1">
                    {toolsMenu.map((tool) => {
                      const Icon = tool.icon;
                      return (
                        <Link
                          key={tool.path}
                          to={tool.path}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-[#008751] hover:bg-gray-50 rounded-lg transition-colors text-left"
                        >
                          <Icon className="w-4 h-4" />
                          {tool.label}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.label}
                  to={link.path}
                  className={`block w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive(link.path)
                    ? 'bg-[#E8F5E9] text-[#008751]'
                    : 'text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  {link.label}
                </Link>
              )
            ))}
            <Link
              to="/power-manager"
              className="block w-full text-left bg-[#C1272D] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#a02024] transition-colors"
            >
              Play Power Manager
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
